/**
 * TaskTypePickerModal — okno wyboru typu zadania (przed formularzem nowego zadania oraz przy
 * akcji „Zmień typ" na karcie). Cienka nakładka na wspólne `RecordTypePickerModal` — to samo okno
 * co przy dokumentach (filtr firmy, wyszukiwanie po kodzie i nazwie, kolumny).
 *
 * Lista: aktywne typy (serwer: `GET /task-types?active=1`) z nazwą firmy. `companyId` zawęża listę
 * do jednej firmy (zmiana typu nie przenosi zadania między firmami), `excludeTypeId` pomija typ
 * obecny. Wybór firmy NIE jest osobnym krokiem — firma wynika z wybranego typu; filtr firmy w oknie
 * startuje od `preferredCompanyId` (aktywna firma użytkownika) albo od firmy zadania przy „Zmień typ".
 *
 * Ulubione typy (gwiazdka + przełącznik „lista podręczna / pełna lista") jak przy dokumentach —
 * przypięcia użytkownika z listy podręcznej (`/quick-list`, rodzaj `task_type`).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type RecordTypeOption,
  RecordTypePickerModal,
} from '@/components/kartoteki/RecordTypePickerModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useTaskTypes } from '@/lib/tasks/task-api';
import { useQuickList } from '@/lib/use-quick-list';

export interface TaskTypePickerModalProps {
  open: boolean;
  onSelect: (taskTypeId: string, companyId: string) => void;
  onClose: () => void;
  /** Nadpisanie etykiety przycisku potwierdzenia — domyślnie „Otwórz formularz". */
  confirmLabel?: string;
  /** Nadpisanie tytułu okna. */
  title?: string;
  /** Pokaż wyłącznie typy tej firmy (zmiana typu na zapisanym zadaniu). */
  companyId?: string;
  /** Pomiń ten typ (obecny typ zadania przy „Zmień typ"). */
  excludeTypeId?: string;
  /** Firma, której typy mają pierwszeństwo (filtr firmy i wstępne zaznaczenie) — aktywna firma użytkownika. */
  preferredCompanyId?: string | null;
}

export function TaskTypePickerModal({
  open,
  onSelect,
  onClose,
  confirmLabel,
  title,
  companyId,
  excludeTypeId,
  preferredCompanyId = null,
}: TaskTypePickerModalProps) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const typesQ = useTaskTypes({ companyId, activeOnly: true });
  const { pinnedIds, isLoading: isQuickListLoading, pin, unpin } = useQuickList('task_type');

  const companies = useMemo(
    () =>
      [...(me?.companies ?? [])]
        .map((c) => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [me?.companies],
  );

  const options = useMemo<RecordTypeOption[]>(() => {
    const companyNameById = new Map(companies.map((c) => [c.id, c.name] as const));
    const all = (typesQ.data ?? [])
      .filter((x) => x.id !== excludeTypeId)
      .map<RecordTypeOption>((x) => ({
        id: x.id,
        name: x.name,
        code: x.code,
        companyId: x.companyId,
        companyName: companyNameById.get(x.companyId) ?? '',
        description: x.description,
      }));
    if (preferredCompanyId === null) return all;
    // Typy preferowanej firmy na początek (wstępne zaznaczenie bierze pierwszy wiersz) —
    // kolejność serwera zostaje w obrębie grup.
    return [
      ...all.filter((x) => x.companyId === preferredCompanyId),
      ...all.filter((x) => x.companyId !== preferredCompanyId),
    ];
  }, [typesQ.data, companies, excludeTypeId, preferredCompanyId]);

  return (
    <RecordTypePickerModal
      open={open}
      onClose={onClose}
      onSelect={onSelect}
      testIdPrefix="task-type-picker"
      types={options}
      isLoading={typesQ.isLoading}
      isError={typesQ.isError}
      companies={companies}
      defaultFilterCompanyId={companyId ?? preferredCompanyId}
      favorites={{ pinnedIds, isLoading: isQuickListLoading, pin, unpin }}
      labels={{
        title: title ?? t('tenant.tasks.typePicker.title'),
        confirm: confirmLabel ?? t('tenant.tasks.typePicker.confirm'),
        allCompaniesHint: t('tenant.tasks.typePicker.hint'),
        empty: t('tenant.tasks.typePicker.empty'),
        colCode: t('tenant.tasks.typePicker.colCode'),
        colName: t('tenant.tasks.typePicker.colName'),
        colCompany: t('tenant.tasks.typePicker.colCompany'),
        colDescription: t('tenant.tasks.typePicker.colDescription'),
        filterCode: t('tenant.tasks.typePicker.filterCode'),
        filterName: t('tenant.tasks.typePicker.filterName'),
        filterCompany: t('tenant.tasks.typePicker.filterCompany'),
        favorites: {
          toggleShowPinned: t('tenant.tasks.typePicker.toggleShowPinned'),
          toggleShowFull: t('tenant.tasks.typePicker.toggleShowFull'),
          pinTooltip: t('tenant.tasks.typePicker.pinTooltip'),
          unpinTooltip: t('tenant.tasks.typePicker.unpinTooltip'),
        },
      }}
    />
  );
}
